import { useState } from 'react';
import {
  RiSearchLine,
  RiCodeLine,
  RiLayoutGridLine,
  RiSparklingLine,
  RiTeamLine,
  RiShieldLine,
  RiQuestionLine,
  RiBookOpenLine,
  RiRocketLine,
  RiArrowRightLine,
  RiExternalLinkLine,
  RiKeyboardLine,
  RiLightbulbLine,
} from '@remixicon/react';
import { Input } from '@/components/ui/input';

const helpCategories = [
  {
    title: 'Getting Started',
    icon: RiRocketLine,
    articles: [
      { title: 'Welcome to Moniva', href: '#welcome', duration: '2 min' },
      { title: 'Connecting Your First Database', href: '#connect-database', duration: '5 min' },
      { title: 'Quick Start Guide', href: '#quick-start', duration: '3 min' },
      { title: 'Understanding the Interface', href: '#interface', duration: '4 min' },
    ],
  },
  {
    title: 'Visual Schema Designer',
    icon: RiLayoutGridLine,
    articles: [
      { title: 'Creating Tables and Columns', href: '#schema-tables', duration: '6 min' },
      { title: 'Defining Relationships', href: '#schema-relationships', duration: '5 min' },
      { title: 'Generating SQL Code', href: '#schema-sql', duration: '3 min' },
      { title: 'Generating TypeScript Types', href: '#schema-typescript', duration: '4 min' },
    ],
  },
  {
    title: 'Query Builder & Editor',
    icon: RiCodeLine,
    articles: [
      { title: 'Visual Query Builder Basics', href: '#query-builder', duration: '7 min' },
      { title: 'Writing SQL Queries', href: '#sql-editor', duration: '5 min' },
      { title: 'Saving and Organizing Queries', href: '#save-queries', duration: '3 min' },
      { title: 'Query History', href: '#query-history', duration: '2 min' },
    ],
  },
  {
    title: 'AI Query Assistant',
    icon: RiSparklingLine,
    articles: [
      { title: 'Using AI to Generate Queries', href: '#ai-basics', duration: '4 min' },
      { title: 'Tips for Better AI Results', href: '#ai-tips', duration: '5 min' },
      { title: 'Understanding AI Limits', href: '#ai-limits', duration: '2 min' },
      { title: 'AI Privacy & Security', href: '#ai-privacy', duration: '3 min' },
    ],
  },
  {
    title: 'Team Collaboration',
    icon: RiTeamLine,
    articles: [
      { title: 'Inviting Team Members', href: '#invite-team', duration: '3 min' },
      { title: 'Roles and Permissions', href: '#roles', duration: '5 min' },
      { title: 'Real-Time Collaboration', href: '#realtime', duration: '4 min' },
      { title: 'Sharing Queries', href: '#share-queries', duration: '2 min' },
    ],
  },
  {
    title: 'Security & Access',
    icon: RiShieldLine,
    articles: [
      { title: 'Database Security Best Practices', href: '#security-practices', duration: '6 min' },
      { title: 'API Keys Management', href: '#api-keys', duration: '4 min' },
      { title: 'Access Logs and Audit Trail', href: '#audit-logs', duration: '5 min' },
      { title: 'SSL/TLS Connections', href: '#ssl-tls', duration: '3 min' },
    ],
  },
];

const quickLinks = [
  { title: 'Keyboard Shortcuts', icon: RiKeyboardLine, href: '#shortcuts' },
  { title: 'Troubleshooting', icon: RiQuestionLine, href: '#troubleshooting' },
  { title: 'FAQ', icon: RiLightbulbLine, href: '#faq' },
  { title: 'Contact Support', icon: RiTeamLine, href: '#support' },
];

export function HelpPage() {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCategories = helpCategories.map(category => ({
    ...category,
    articles: category.articles.filter(article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(category => category.articles.length > 0);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 px-8 py-8 border-b">
        <div className="max-w-4xl mx-auto">
          <h1 className="mb-2 text-2xl font-semibold">Help Center</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            Find guides and answers to common questions
          </p>

          {/* Search */}
          <div className="relative">
            <RiSearchLine className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-8 py-8 overflow-y-auto">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Quick Links */}
          {!searchQuery && (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {quickLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <a
                    key={link.title}
                    href={link.href}
                    className="flex items-center gap-2 p-3 transition-colors border rounded-lg hover:bg-muted/50"
                  >
                    <Icon className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{link.title}</span>
                  </a>
                );
              })}
            </div>
          )}

          {/* Help Categories */}
          <div className="space-y-4">
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category) => {
                const Icon = category.icon;
                return (
                  <div key={category.title} className="border rounded-lg">
                    <div className="p-4 border-b">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <h3 className="font-medium">{category.title}</h3>
                        <span className="text-xs text-muted-foreground">
                          ({category.articles.length})
                        </span>
                      </div>
                    </div>
                    <div className="p-2">
                      {category.articles.map((article) => (
                        <a
                          key={article.title}
                          href={article.href}
                          className="flex items-center justify-between p-2 transition-colors rounded hover:bg-muted"
                        >
                          <span className="text-sm">{article.title}</span>
                          <span className="text-xs text-muted-foreground">
                            {article.duration}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <RiSearchLine className="w-8 h-8 mb-3 text-muted-foreground" />
                <h3 className="mb-1 font-medium">No results found</h3>
                <p className="text-sm text-muted-foreground">
                  Try different keywords or browse categories above
                </p>
              </div>
            )}
          </div>

          {/* Additional Resources */}
          {!searchQuery && (
            <div className="pt-4 mt-6 border-t">
              <h3 className="mb-3 text-sm font-medium">Need More Help?</h3>
              <div className="grid gap-2 sm:grid-cols-2">
                <a
                  href="https://docs.usemoniva.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 transition-colors border rounded-lg hover:bg-muted"
                >
                  <div className="flex items-center gap-2">
                    <RiBookOpenLine className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Full Documentation</span>
                  </div>
                  <RiExternalLinkLine className="w-3 h-3 text-muted-foreground" />
                </a>

                <a
                  href="https://discord.gg/moniva"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 transition-colors border rounded-lg hover:bg-muted"
                >
                  <div className="flex items-center gap-2">
                    <RiTeamLine className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Join Community</span>
                  </div>
                  <RiExternalLinkLine className="w-3 h-3 text-muted-foreground" />
                </a>

                <a
                  href="mailto:support@usemoniva.com"
                  className="flex items-center justify-between p-3 transition-colors border rounded-lg hover:bg-muted"
                >
                  <div className="flex items-center gap-2">
                    <RiQuestionLine className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Email Support</span>
                  </div>
                  <RiArrowRightLine className="w-3 h-3 text-muted-foreground" />
                </a>

                <a
                  href="https://github.com/moniva/moniva/issues"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 transition-colors border rounded-lg hover:bg-muted"
                >
                  <div className="flex items-center gap-2">
                    <RiLightbulbLine className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Feature Requests</span>
                  </div>
                  <RiExternalLinkLine className="w-3 h-3 text-muted-foreground" />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
